import { ValueNarrative, ValueNarrativeCategory } from '../../../../../queries/useGetValueNarrativesQuery'
import { alpha, Fade, SvgIcon, Table, TableCell, TableContainer, TableHead, Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import { ReactComponent as RightArrowIcon } from '../../../../../../../assets/images/arrows/right.svg'
import { ReactComponent as CircleIcon } from '../../../../../../../assets/images/circle.svg'
import { ReactComponent as ArrowDownIcon } from '../../../../../../../assets/images/arrows/down.svg'
import IconButton from '@mui/material/IconButton'
import React, { useState } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { SortableTable } from '../../../../../../../shared/components/SortableTable'
import { useValueNarrativeColorBySubpopulation } from '../../../../../../../views/ValueCompendium/tabs/ValueNarrativesTab/hooks/useValueNarrativeColorBySubpopulation'
import ValueNarrativeArchiveIcon from '../../../../../components/ValueNarrativeIcon/ValueNarrativeArchiveIcon'
import ValueNarrativeLockIcon from '../../../../../components/ValueNarrativeIcon/ValueNarrativeLockIcon'
import ValueNarrativeActions from '../../../../ValueNarrativeActions/ValueNarrativeActions'

type TableViewProps = {
    category: ValueNarrativeCategory
    handleSelectNarrative: (categoryId: string, valueNarrativeId: string) => void
    isReorderModeActive?: boolean
    onReorderNarratives?: (newNarratives: ValueNarrative[]) => void
}

const TableView = ({ category, handleSelectNarrative, isReorderModeActive, onReorderNarratives }: TableViewProps) => {
    const [areNarrativesVisible, setAreNarrativesVisible] = useState(!category.isPseudoCategory)

    const { getValueNarrativeColorBySubpopulation } = useValueNarrativeColorBySubpopulation()

    // SortableTable requires each row to have an `id` field; ValueNarrative uses `_id`.
    const sortableNarratives = category.narratives.map((narrative) => ({ ...narrative, id: narrative._id }))

    const handleSortNarratives = (sortingEvent) => {
        const { active, over } = sortingEvent

        if (!over || active.id === over.id) {
            return
        }

        const indexOfDestinationRow = sortingEvent.over.data.current.sortable.index
        const indexOfSourceRow = sortingEvent.active.data.current.sortable.index
        const newNarratives = arrayMove(category.narratives, indexOfSourceRow, indexOfDestinationRow)
        onReorderNarratives?.(newNarratives)
    }

    return (
        <TableContainer
            sx={{
                marginBottom: 3,
                '.MuiTableRow-root': {
                    '&:nth-of-type(odd)': {
                        backgroundColor: 'figma.grayscale.table1',
                    },
                    '&:nth-of-type(even)': {
                        backgroundColor: 'figma.grayscale.table2',
                    },
                },
                '.MuiTableCell-root': {
                    border: 'none',
                },
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableCell />
                    <TableCell>
                        <Stack
                            direction="row"
                            alignItems="center"
                            width="max-content"
                            gap={1}
                            mb={1.5}
                            color={category.isPseudoCategory ? 'figma.grayscale.tier4' : 'figma.grayscale.textHier1'}
                            sx={
                                category.isPseudoCategory
                                    ? {
                                          cursor: 'pointer',
                                          svg: {
                                              pointerEvents: 'none',
                                          },
                                      }
                                    : {}
                            }
                            onClick={() => {
                                if (category.isPseudoCategory) setAreNarrativesVisible(!areNarrativesVisible)
                            }}
                        >
                            <Typography color="inherit" fontSize="18px !important" fontWeight={600}>
                                {category.name}
                            </Typography>
                            {category.isPseudoCategory && (
                                <SvgIcon
                                    component={ArrowDownIcon}
                                    inheritViewBox
                                    color="inherit"
                                    sx={{
                                        fontSize: '16px',
                                        transform: areNarrativesVisible ? 'rotate(180deg)' : '',
                                    }}
                                />
                            )}
                        </Stack>
                    </TableCell>
                    <TableCell />
                </TableHead>
                <Fade in={areNarrativesVisible} timeout={1000}>
                    <SortableTable.Body
                        data={sortableNarratives}
                        onSort={handleSortNarratives}
                        disabled={!isReorderModeActive}
                    >
                        {({ rowData: narrative, index: narrativeIndex, rowProps }) => {
                            const onValueNarrativeClick = () => {
                                handleSelectNarrative(narrative.categoryId, narrative._id)
                            }

                            const colorBySubpopulation = getValueNarrativeColorBySubpopulation(narrative.subpopulation)

                            return (
                                <SortableTable.Row {...rowProps}>
                                    <TableCell
                                        align="left"
                                        width={'32px'}
                                        sx={{
                                            borderTopLeftRadius: narrativeIndex === 0 ? 14 : 0,
                                            borderBottomLeftRadius:
                                                narrativeIndex === category.narratives.length - 1 ? 14 : 0,
                                            paddingRight: 0,
                                        }}
                                    >
                                        <Typography
                                            // @ts-expect-error theme type
                                            variant="body9"
                                            color="figma.grayscale.tier3"
                                        >
                                            {narrativeIndex + 1}.
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="left" width="100%">
                                        <Typography
                                            // @ts-expect-error theme type
                                            variant="body4"
                                            color="figma.grayscale.textHier2"
                                        >
                                            {narrative.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            borderTopRightRadius: narrativeIndex === 0 ? 14 : 0,
                                            borderBottomRightRadius:
                                                narrativeIndex === category.narratives.length - 1 ? 14 : 0,
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                            {!!narrative.archived && (
                                                <Stack mr={0.25}>
                                                    <ValueNarrativeArchiveIcon archived={narrative.archived} />
                                                </Stack>
                                            )}
                                            {!narrative.archived && !!narrative.locked && (
                                                <Stack mr={0.25}>
                                                    <ValueNarrativeLockIcon locked={narrative.locked} />
                                                </Stack>
                                            )}
                                            <Stack mr={0.75}>
                                                <ValueNarrativeActions valueNarrative={narrative} />
                                            </Stack>
                                            <SvgIcon
                                                fontSize="small"
                                                component={CircleIcon}
                                                inheritViewBox
                                                sx={{
                                                    fontSize: '14px',
                                                    color: colorBySubpopulation,
                                                    '& > circle': {
                                                        fill: alpha(colorBySubpopulation, 0.1),
                                                    },
                                                }}
                                            />
                                            <IconButton size="large" onClick={onValueNarrativeClick}>
                                                <SvgIcon
                                                    component={RightArrowIcon}
                                                    inheritViewBox
                                                    fontSize="small"
                                                    color="inherit"
                                                    sx={{
                                                        fontSize: '20px',
                                                        color: 'figma.primary',
                                                    }}
                                                />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </SortableTable.Row>
                            )
                        }}
                    </SortableTable.Body>
                </Fade>
            </Table>
        </TableContainer>
    )
}

export default TableView
